/**
 * Storefront logic for a membership's pricing-option SET: which options a
 * shopper is shown, what the payment-mode choice offers, which cart fields a
 * commitment line carries, and whether a membership is on sale at all.
 *
 * `membershipLabels` describes one option; this describes the menu. Both are
 * pure — the only runtime import is the pricing-type enum every cart line is
 * built from — so the popover, the product card and their tests all read the
 * same rules.
 *
 * Two contracts constrain everything here:
 *
 *   1. Term length and payment lane come from `commitmentMonths` /
 *      `paymentMode`, never from the pricing key. Keys are a lookup
 *      convention; reading meaning out of one breaks when a format moves.
 *
 *   2. A product with no commitment cells must behave EXACTLY as it did before
 *      commitments existed. Every function below either passes such a product
 *      through untouched or is not reached for it at all — a membership sold
 *      years ago cannot start rendering differently because a neighbouring
 *      gym switched to commitment pricing.
 */

import { wallClockParts, type PaymentMode } from "@lemonadetraining/contracts";
import {
  describeCommitmentOption,
  isCommitmentOption,
  optionPaymentMode,
  pricingOptionSortValue,
  type PricingOptionLike,
} from "./membershipLabels";
import * as PRICING from "../constants/Pricing";

/** The subset of a pricing option the storefront reads. */
export interface PlanOption extends PricingOptionLike {
  /** Absent means on sale — see `activePlanEntries`. */
  active?: boolean;
  priceID: string;
  /**
   * Server-written, and only on an upfront cell whose term rolls onward: the
   * monthly price billed once the committed term is over. Read to tell the
   * buyer what continuing will cost them; never written here.
   */
  rollMonthlyPriceID?: string;
}

export type PlanOptions = Record<string, PlanOption>;

/** One option with the key it is stored under. */
export type PlanEntry = [string, PlanOption];

/**
 * The order the payment modes are offered in. Monthly leads because it is the
 * lower barrier to entry and the mode a shopper arriving from a monthly price
 * on the card expects to land on.
 */
export const PAYMENT_MODE_ORDER: PaymentMode[] = ["monthly", "upfront"];

/**
 * Whether a value is one of the payment modes. Derived from the order above so
 * a third mode would be recognized everywhere the moment it is offered, rather
 * than in every place that happens to compare against two string literals.
 */
export function isPaymentMode(value: string): value is PaymentMode {
  return (PAYMENT_MODE_ORDER as string[]).includes(value);
}

/**
 * Every option currently on sale, in the order the product stores them.
 *
 * A cell with NO `active` field is on sale. The server's sell-side rule is the
 * one this mirrors — `sellableCellForPrice` in `functions/matrixProvisioning.js`
 * accepts a cell when `active !== false` — so a cell written before the flag
 * existed still buys. A truthy check here would hide it from the storefront
 * while the server happily sold it, which is the worse half of the mismatch:
 * the shopper cannot see a plan their gym is still selling.
 *
 * (`resolveRollTargetKey`, further down that same server file, uses a truthy
 * check instead. That is the server disagreeing with itself; it decides which
 * cell a term ROLLS onto, not what is for sale. Storefront parity follows the
 * sell-side rule, because "can I buy this?" is the question being answered.)
 */
export function activePlanEntries(options: PlanOptions | undefined | null): PlanEntry[] {
  return Object.entries(options || {}).filter(([, option]) => option && option.active !== false);
}

/**
 * The payment modes this product actually sells a commitment on. A gym that
 * only offers one gets one entry — the caller must not render a two-way choice
 * whose second side is empty.
 */
export function availablePaymentModes(options: PlanOptions | undefined | null): PaymentMode[] {
  const entries = activePlanEntries(options);
  return PAYMENT_MODE_ORDER.filter((mode) =>
    entries.some(([, option]) => isCommitmentOption(option) && optionPaymentMode(option) === mode)
  );
}

/**
 * The options to render for a chosen payment mode.
 *
 * `mode: null` means the product has no payment-mode dimension: every active
 * option, in the product's own order, untouched. That is the grandfathered
 * path and it must stay a pass-through.
 *
 * With a mode chosen, commitment cells are filtered to that lane while
 * everything else — the no-commitment monthly price, legacy multi-month
 * prices — stays visible in both, because none of it has a second lane to
 * hide in. The result is ordered by term so the ladder reads shortest-first
 * regardless of how the options happen to be stored.
 */
export function visiblePlanEntries(
  options: PlanOptions | undefined | null,
  mode: PaymentMode | null
): PlanEntry[] {
  const entries = activePlanEntries(options);
  if (mode === null) return entries;

  return entries
    .filter(([, option]) => !isCommitmentOption(option) || optionPaymentMode(option) === mode)
    .sort(([, a], [, b]) => pricingOptionSortValue(a) - pricingOptionSortValue(b));
}

/**
 * The cheapest option per unit among the ones on screen — per month for
 * memberships, per visit for visit cards. Computed over the VISIBLE entries so
 * the answer belongs to the mode the shopper is looking at: the cheapest
 * upfront term is not the best value on offer while the monthly lane is shown.
 * Ties go to the first, matching the order the rows render in.
 */
export function bestValuePlanKey(entries: PlanEntry[]): string | null {
  let bestKey: string | null = null;
  let lowestPerUnit = Infinity;

  for (const [key, option] of entries) {
    const { perUnitAmount } = describeCommitmentOption(option);
    if (perUnitAmount < lowestPerUnit) {
      lowestPerUnit = perUnitAmount;
      bestKey = key;
    }
  }

  return bestKey;
}

/**
 * The commitment metadata a cart line carries so the checkout review can show
 * what the shopper is agreeing to without re-reading the product.
 *
 * The PRESENCE of `commitmentMonths` is the single discriminator downstream:
 * a line without it is an ordinary purchase and behaves exactly as it always
 * has. So a non-commitment option returns nothing at all rather than a set of
 * defaults that would make a plain monthly membership look like a contract.
 *
 * Only what the shopper is choosing lives here. What happens WHEN a term ends
 * — roll or expire, and onto which rate — is decided by the server from the
 * product's own configuration when the schedule is bound
 * (`functions/matrixProvisioning.js`), so the storefront neither models it nor
 * carries it: a second answer computed here could only ever agree by luck.
 */
export interface CommitmentCartFields {
  commitmentMonths?: number;
  paymentMode?: PaymentMode;
  /** What the member pays across the whole term. */
  commitmentTotal?: number;
}

export function commitmentCartFields(option: PlanOption | undefined | null): CommitmentCartFields {
  if (!option || !isCommitmentOption(option)) return {};

  const { commitmentMonths, paymentMode, totalAmount } = describeCommitmentOption(option);
  return { commitmentMonths, paymentMode, commitmentTotal: totalAmount };
}

/** What a cart line needs to know about the product it is a line for. */
export interface CartLineProduct {
  productId: string;
  name: string;
  coachName?: string;
  coachUID?: string;
  coverArt?: string | null;
  stripeAccountID?: string | null;
  isLemonadeProduct?: boolean;
  /**
   * The gym that sells it. Carried so checkout can find the product document
   * again — a membership lives at `Boxes/{boxID}/Memberships/{productId}`, and
   * the product id alone does not locate it. Optional because the grandfathered
   * non-box surfaces have no box to name.
   */
  boxID?: string;
}

/** A membership cart line, as the cart slice stores it. */
export interface MembershipCartLine extends CommitmentCartFields {
  title: string;
  programUID: string;
  coachName?: string;
  coachUID?: string;
  coverArt?: string | null;
  pricing: { type: string; amount: number };
  priceID: string;
  selectedOptionKey: string;
  stripeAccountID: string | null;
  isLemonadeProduct: boolean;
  boxID?: string;
}

/**
 * The one place a membership cart line is built, commitment enrichment
 * included. Two surfaces add memberships — the plan popover and the card's own
 * button when a gym sells exactly one plan — and a line assembled twice is a
 * line that eventually differs: the enrichment reaching only one of them would
 * send a commitment to checkout looking like an ordinary membership.
 *
 * Requires the chosen option, because the option IS what makes this a
 * membership line. The card's other dispatch — a grandfathered product with no
 * option map at all, selling at its top-level price — has no option to pass and
 * builds its own legacy payload: absence of an option means there is nothing to
 * enrich, by design.
 */
export function buildMembershipCartLine(
  product: CartLineProduct,
  option: PlanOption,
  optionKey: string
): MembershipCartLine {
  return {
    title: product.name,
    programUID: product.productId,
    coachName: product.coachName,
    coachUID: product.coachUID,
    coverArt: product.coverArt,
    pricing: {
      // "one_time" is the visit-card interval and is truthy, so it must NOT be
      // treated as a subscription; every other interval is recurring and has to
      // reach Stripe in subscription mode.
      type:
        option.interval && option.interval !== "one_time"
          ? PRICING.TYPE.subscription
          : PRICING.TYPE.oneTime,
      amount: option.amount,
    },
    priceID: option.priceID,
    selectedOptionKey: optionKey,
    stripeAccountID: product.stripeAccountID || null,
    isLemonadeProduct: product.isLemonadeProduct || false,
    boxID: product.boxID,
    // Commitment metadata travels WITH the line so checkout can show what the
    // shopper agreed to without re-reading the product. Absent for an ordinary
    // purchase, which is what marks it as one.
    ...commitmentCartFields(option),
  };
}

/**
 * Whether a membership has been taken off sale entirely: it defines pricing
 * options and every one of them has been EXPLICITLY withdrawn (`active: false`).
 *
 * The explicitness matters as much here as it does in `activePlanEntries`, and
 * in the same direction: a cell that never carried the flag has not been
 * withdrawn by anyone, so a product made only of such cells is on sale, not
 * unavailable. Reading a missing flag as "off" would post "Not available right
 * now" over a membership the gym is still selling and the server would still
 * take money for.
 *
 * This is NOT the same as a product that never had an option map. That one is
 * grandfathered — its top-level price is the real price and it still sells.
 * Conflating the two would either withdraw memberships that are perfectly on
 * sale or, far worse, keep selling a withdrawn one at a stale price the gym
 * has stopped honouring.
 */
export function hasWithdrawnPlansOnly(options: PlanOptions | undefined | null): boolean {
  const entries = Object.entries(options || {});
  return entries.length > 0 && entries.every(([, option]) => option && option.active === false);
}

/**
 * User-facing strings for the commitment storefront surfaces, in one place so
 * a translation pass has a single file to work from. English only today —
 * these apps carry no i18n framework, so this is the seam, not the solution.
 */
const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = {
  monthly: "Pay monthly",
  upfront: "Pay upfront",
};

/** How the footer names each lane, mid-sentence. */
const PAYMENT_MODE_PHRASES: Record<PaymentMode, string> = {
  monthly: "billed monthly",
  upfront: "paid upfront",
};

/**
 * Spoken when the lane changes. Switching the toggle silently re-prices every
 * row and clears the selection — plainly visible, and invisible to a shopper
 * using a screen reader unless something says so.
 */
const PAYMENT_MODE_ANNOUNCEMENTS: Record<PaymentMode, string> = {
  monthly: "Showing pay monthly prices",
  upfront: "Showing pay upfront prices",
};

export const COMMITMENT_STOREFRONT_COPY = {
  /** Names the payment-mode choice for screen readers. */
  paymentModeLabel: "How you want to pay",
  /** The toggle's segments, one per mode on offer. */
  paymentModeLabels: PAYMENT_MODE_LABELS,
  paymentModePhrases: PAYMENT_MODE_PHRASES,
  /** Announced to screen readers when the lane changes. */
  paymentModeAnnouncements: PAYMENT_MODE_ANNOUNCEMENTS,
  /** Closes the footer sentence: the full terms are one step away, not hidden. */
  footerDetails: "details at checkout",
  unavailable: "Not available right now",
  unavailableHint: "This membership isn't on sale at the moment — ask your gym when it's back.",
} as const;

/** How a euro amount reads on these surfaces. */
const euros = (amount: number): string => `€${amount.toFixed(2)}`;

/**
 * The money actually in play for one option, as every surface that shows a
 * single figure must read it.
 *
 * One rule, one place: wherever the INSTALMENT is what the shopper is about to
 * be charged — the card's price line, the Add-to-Cart button, a cart line — the
 * instalment is what is shown, with the term named beside it. The whole-term
 * total belongs to the plan row and the honesty footer, which have the room to
 * say what it is. Showing €540 where €45 will be charged is as dishonest as
 * showing €45 where €540 will be, and the two surfaces disagreeing is worse
 * than either: a shopper who reads both learns to trust neither.
 */
export interface OptionInstalment {
  /** The figure charged in one go: a monthly term's per-month price, an upfront term's single charge, a plain option's price. */
  amount: number;
  /** "/mo" when that figure repeats every month; "" when it is the entire charge. */
  perPeriodSuffix: string;
  /** "12-month term" for a commitment cell; null when there is no term to name. */
  termQualifier: string | null;
}

export function optionInstalment(option: PlanOption): OptionInstalment {
  const { isCommitment, commitmentMonths, paymentMode, totalAmount, perUnitAmount } =
    describeCommitmentOption(option);

  if (!isCommitment) {
    return { amount: totalAmount, perPeriodSuffix: "", termQualifier: null };
  }

  const termQualifier = `${commitmentMonths}-month term`;
  return paymentMode === "monthly"
    ? { amount: perUnitAmount, perPeriodSuffix: "/mo", termQualifier }
    : { amount: totalAmount, perPeriodSuffix: "", termQualifier };
}

/**
 * The one quiet line under the plan rows: the term, how it is paid, and what
 * it costs in total. It exists so the commitment is never a surprise at
 * checkout — the shopper agrees to it here, with the number in front of them.
 *
 * Returns null when there is nothing to commit to, which is the no-commitment
 * row's whole point.
 */
export function commitmentFooterText(option: PlanOption | null | undefined): string | null {
  if (!option || !isCommitmentOption(option)) return null;

  const { commitmentMonths, paymentMode, totalAmount } = describeCommitmentOption(option);
  return (
    `${commitmentMonths}-month commitment, ${COMMITMENT_STOREFRONT_COPY.paymentModePhrases[paymentMode]} · ` +
    `total ${euros(totalAmount)} · ${COMMITMENT_STOREFRONT_COPY.footerDetails}`
  );
}

/* ==========================================================================
 * Checkout review — what a commitment actually costs, spelled out
 *
 * The plan popover sells; this tells the buyer what they are signing. It is
 * the last screen before money moves, so every figure on it has to be one the
 * gym is really charging, and any figure we cannot stand behind has to be
 * absent rather than guessed. That single rule explains every `null` below.
 * ======================================================================== */

/**
 * How a euro amount reads on the review, and the ONLY formatter it uses.
 *
 * The same function as the storefront footer's `euros`, under a public name —
 * one implementation, so the footer and the review cannot drift apart.
 */
export const formatEuros = euros;

/** Deterministic month names — see `formatCommitmentDate`. */
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * The date a term that starts on `start` runs to.
 *
 * A short target month clamps to its last day (31 January + 1 month is 28
 * February, not 3 March), which is both what a reader expects and what a
 * naive `setMonth` would silently get wrong by rolling into the next month.
 */
export function addMonths(start: Date, months: number): Date {
  const end = new Date(start.getTime());
  const dayOfMonth = end.getDate();
  end.setMonth(end.getMonth() + months);
  if (end.getDate() < dayOfMonth) {
    // Overflowed into the following month; step back onto the last day of the
    // month we actually meant.
    end.setDate(0);
  }
  return end;
}

/**
 * "4 August 2027".
 *
 * Formatted from an explicit month table rather than `toLocaleDateString`,
 * because the runtime's locale data is not something a contract date should
 * depend on: a CI image built without full ICU quietly renders month names as
 * numbers, and this is a date the buyer is being held to.
 */
export function formatCommitmentDate(date: Date, timeZone?: string | null): string {
  const gym = timeZone ? wallClockParts(date, timeZone) : null;
  if (gym) return `${gym.day} ${MONTH_NAMES[gym.month - 1]} ${gym.year}`;
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * The gym's plain month-to-month price, if it sells one: the active,
 * non-commitment, single-month cell. It is the honest baseline for "what does
 * committing save me" — comparing against a different term's rate would
 * manufacture a saving out of a discount the buyer could have had anyway.
 *
 * Null when the gym sells no such plan, which is a real configuration and not
 * an error. There is simply nothing to compare against, and the review says
 * nothing rather than inventing a comparison.
 */
export function noCommitmentMonthlyAmount(options: PlanOptions | undefined | null): number | null {
  for (const [, option] of activePlanEntries(options)) {
    if (isCommitmentOption(option)) continue;
    if (option.interval !== "month") continue;
    if ((option.intervalCount ?? 1) !== 1) continue;
    return option.amount;
  }
  return null;
}

/**
 * A product's effective end-of-term behavior.
 *
 * Mirrors the server's `effectiveEndBehavior` exactly, including its default:
 * anything that is not the literal "expire" means the membership rolls onward.
 * A truthiness check here would read a product that never set the field as
 * "expire" and promise the buyer their membership stops on its own — the one
 * error in this pair that ends with someone unknowingly still being billed, or
 * unknowingly locked out.
 */
export type CommitmentEndBehavior = "roll_monthly" | "expire";

export function effectiveEndBehavior(value: unknown): CommitmentEndBehavior {
  return value === "expire" ? "expire" : "roll_monthly";
}

/** The freshly-read product fields the review needs. */
export interface CommitmentReviewProduct {
  pricingOptions?: PlanOptions | null;
  endBehavior?: string | null;
  rollTargetRate?: string | null;
}

/**
 * Everything the review block states about one commitment line.
 *
 * `null` on a field always means "we could not establish this", never "zero"
 * and never "not applicable". The renderer omits those rows; it must never
 * substitute a default, because every one of these is a number the gym will
 * act on.
 */
export interface CommitmentReview {
  commitmentMonths: number;
  paymentMode: PaymentMode;
  /** Charged in one go — one month on the monthly lane, the whole term upfront. */
  instalmentAmount: number;
  /** How many such charges make up the term. */
  instalmentCount: number;
  /** Paid across the whole term. Null when the line predates the field and no fresh config resolved it. */
  total: number | null;
  /** The month-to-month price the saving is measured against; null when the gym sells none. */
  comparisonMonthly: number | null;
  /** Term total subtracted from paying month-to-month for the same time. Null when incomputable. */
  savings: number | null;
  /** Null when the product could not be re-read — an unknown ending is not a promise we may make. */
  endBehavior: CommitmentEndBehavior | null;
  /** What continuing costs per month once the term is up; null when the term expires or the rate is unresolved. */
  rollMonthlyAmount: number | null;
  /** The last day of the committed term, if it started today. */
  endDate: Date;
  /** Whether the figures came from the product as it stands right now. */
  fromFreshConfig: boolean;
  /** The gym's price for this plan moved since it went into the cart. */
  priceChanged: boolean;
}

/**
 * Describe one cart line as the review renders it, preferring the product's
 * CURRENT configuration over the snapshot the cart is holding.
 *
 * The cart line deliberately carries only what the shopper chose — term,
 * lane, total — and nothing about how the term ends, because a second answer
 * computed at add-to-cart time could only agree with the server's by luck. So
 * the ending, the roll rate and the comparison price are read fresh here, and
 * are simply absent when the read did not happen.
 *
 * Passing `product: null` (the read failed, or the line has no gym to read
 * from) is a supported, honest state: the review still shows what the buyer
 * chose and what it costs, and says nothing it cannot source.
 *
 * Returns null for a line that is not a commitment at all, which is what keeps
 * an ordinary membership rendering exactly as it always has.
 */
export function describeCommitmentReview(
  line: MembershipCartLine,
  product: CommitmentReviewProduct | null | undefined,
  now: Date = new Date()
): CommitmentReview | null {
  if (!isCommitmentLine(line)) return null;

  const options = product?.pricingOptions;
  const candidate = options ? options[line.selectedOptionKey] : undefined;
  // A cell that has stopped being a commitment (or vanished) cannot re-price
  // this line — the buyer is holding a term the gym no longer offers under
  // that key, and the cart's own snapshot is the more truthful record of what
  // they were shown.
  const fresh = candidate && isCommitmentOption(candidate) ? candidate : null;

  let commitmentMonths: number;
  let paymentMode: PaymentMode;
  let instalmentAmount: number;
  let total: number | null;

  if (fresh) {
    const described = describeCommitmentOption(fresh);
    const instalment = optionInstalment(fresh);
    commitmentMonths = described.commitmentMonths;
    paymentMode = described.paymentMode;
    instalmentAmount = instalment.amount;
    total = described.totalAmount;
  } else {
    commitmentMonths = line.commitmentMonths;
    paymentMode = line.paymentMode ?? "monthly";
    instalmentAmount = line.pricing.amount;
    total = line.commitmentTotal ?? null;
  }

  const instalmentCount = paymentMode === "upfront" ? 1 : commitmentMonths;

  const comparisonMonthly = fresh ? noCommitmentMonthlyAmount(options) : null;
  const savings =
    comparisonMonthly !== null && total !== null
      ? Number((comparisonMonthly * commitmentMonths - total).toFixed(2))
      : null;

  // The ending is a claim about the gym's configuration, so it is made only
  // from a configuration we actually read.
  const endBehavior = product ? effectiveEndBehavior(product.endBehavior) : null;

  let rollMonthlyAmount: number | null = null;
  if (endBehavior === "roll_monthly" && fresh) {
    if (paymentMode === "monthly") {
      // A monthly-billed term keeps billing its own price after the term —
      // the server rolls it onto the very price it is already on.
      rollMonthlyAmount = instalmentAmount;
    } else if (fresh.rollMonthlyPriceID) {
      const target = activePlanEntries(options).find(
        ([, option]) => option.priceID === fresh.rollMonthlyPriceID
      );
      rollMonthlyAmount = target ? target[1].amount : null;
    }
  }

  return {
    commitmentMonths,
    paymentMode,
    instalmentAmount,
    instalmentCount,
    total,
    comparisonMonthly,
    savings,
    endBehavior,
    rollMonthlyAmount,
    endDate: addMonths(now, commitmentMonths),
    fromFreshConfig: fresh !== null,
    priceChanged: fresh !== null && instalmentAmount !== line.pricing.amount,
  };
}

/** What the review states about one rolling month-to-month line. */
export interface MonthlyReview {
  /** Charged every month, from the gym's configuration where it could be read. */
  monthlyAmount: number;
  /** Whether that figure came from the product as it stands right now. */
  fromFreshConfig: boolean;
  /** The gym's price for this plan moved since it went into the cart. */
  priceChanged: boolean;
}

/**
 * Describe one non-committing membership line as its review renders it.
 *
 * Deliberately narrower than its commitment sibling: with no term there is no
 * total, no saving, no end date and no ending to state. Returns null for a
 * commitment line so the two panels can never both describe one purchase.
 */
export function describeMonthlyReview(
  line: MembershipCartLine,
  product: CommitmentReviewProduct | null | undefined
): MonthlyReview | null {
  if (isCommitmentLine(line)) return null;

  const options = product?.pricingOptions;
  const candidate = options ? options[line.selectedOptionKey] : undefined;
  // A cell that has since become a commitment cannot re-price this line: the
  // buyer holds a plain month the gym no longer offers under that key, and the
  // cart's own snapshot is the truthful record of what they were shown.
  const fresh = candidate && !isCommitmentOption(candidate) ? candidate : null;
  const monthlyAmount = fresh ? fresh.amount : line.pricing.amount;

  return {
    monthlyAmount,
    fromFreshConfig: fresh !== null,
    priceChanged: fresh !== null && monthlyAmount !== line.pricing.amount,
  };
}

/**
 * Whether one cart line is a commitment — the single definition of the rule.
 * Everything that gates on "does this need consent" filters or tests with
 * this, so the consent UI and the rendered review blocks cannot disagree
 * about what counts.
 */
export function isCommitmentLine<T extends { commitmentMonths?: number }>(
  item: T | null | undefined
): item is T & { commitmentMonths: number } {
  return Boolean(item && item.commitmentMonths);
}

/**
 * A gym membership that renews and carries no fixed term.
 *
 * The single definition of the rule, for the same reason `isCommitmentLine` is:
 * a one-off visit card is deliberately excluded — there is no continuing
 * service for an early-start waiver to be about — and that exclusion must be
 * stated once, not restated wherever it happens to be needed.
 */
export function isRecurringGymLine(item: MembershipCartLine | null | undefined): boolean {
  return Boolean(
    item &&
    item.boxID &&
    !isCommitmentLine(item) &&
    item.pricing?.type === PRICING.TYPE.subscription
  );
}

/**
 * The review's user-facing strings, in one place for the same reason
 * `COMMITMENT_STOREFRONT_COPY` is: a translation pass should have one file to
 * work from. The consent wording and the withdrawal notice are deliberately
 * NOT here — those are published by the server and rendered exactly as served,
 * and a copy of them in this bundle is the one thing that must never exist.
 */
export const COMMITMENT_CHECKOUT_COPY = {
  reviewHeading: "Your commitment",
  totalLabel: "Total over the term",
  savingsLabel: "You save",
  paymentsLabel: "Payments",
  endsLabel: "Commitment ends",
  afterLabel: "After that",
  termLabel: "Term",
  /** Said when the figures are the cart's own snapshot rather than today's configuration. */
  staleConfigNote: "We couldn't check this plan's latest details with your gym just now.",
  /** Said when re-reading the product turned up a different price. */
  priceChangedNote:
    "Your gym has updated this plan's price. The details shown are the current ones — " +
    "to buy this plan, remove it from your cart and add it again.",
  /** Roll and expire, in the buyer's terms rather than the schema's. */
  rollsWithRate: (rate: string) => `Your membership continues month to month at ${rate}/month.`,
  rollsUnknownRate: "Your membership continues month to month until you cancel it.",
  expires: "Your membership ends. Nothing is charged after that.",
  endingUnknown: "Ask your gym what happens when the term ends.",
  /**
   * The closure disclosure. It matters most to someone paying a year upfront,
   * who is otherwise handing over the money with no stated answer to the one
   * question that should worry them.
   */
  closureHeading: "If your gym closes",
  closureBody:
    "If your gym closes or stops operating, your remaining payments stop. Anything you have paid " +
    "in advance for time you have not used is refunded to you.",
  /** Names the consent control for assistive technology, above the served wording. */
  consentHeading: "Starting your membership",
  /**
   * The server classified a cart line as a commitment the client did not —
   * stale cart data. There is no consent checkbox on screen to point at, so
   * the only honest instruction is the one that refreshes the line itself.
   */
  staleCommitmentLine:
    "Your gym has updated this membership since it was added. " +
    "Please remove it from your cart and add it again.",
  withdrawalHeading: "Your right to cancel",
  /** While the served copy is on its way, and when it never arrives. */
  copyLoading: "Loading the membership terms…",
  copyFailed: "We couldn't load the membership terms. Please try again.",
  copyRetry: "Try again",
} as const;

/**
 * The monthly review's strings, beside its commitment sibling so a translation
 * pass still has one file. Every row the two panels share is taken BY REFERENCE
 * rather than retyped: a second wording that says almost the same thing is how
 * two panels start describing the same disclosure differently.
 */
export const MONTHLY_CHECKOUT_COPY = {
  reviewHeading: "Your membership",
  termLabel: COMMITMENT_CHECKOUT_COPY.termLabel,
  termValue: "Monthly, no fixed term",
  paymentsLabel: COMMITMENT_CHECKOUT_COPY.paymentsLabel,
  payments: (amount: string) => `Monthly payments of ${amount}, starting today`,
  renewsLabel: "Renews",
  renewsValue: "Every month until you cancel",
  cancellingLabel: "Cancelling",
  /* States the cancel path as it actually behaves: cancel_at_period_end, with
     no proration, no refund and no undo. An inaccurate sentence here is a
     representation made at the point of sale. */
  cancellingValue:
    "Cancel anytime. You keep access until the end of the period you have already paid for, " +
    "then your membership stops. No further payments are taken, and unused time is not refunded.",
  closureHeading: COMMITMENT_CHECKOUT_COPY.closureHeading,
  closureBody: COMMITMENT_CHECKOUT_COPY.closureBody,
  staleConfigNote: COMMITMENT_CHECKOUT_COPY.staleConfigNote,
  priceChangedNote: COMMITMENT_CHECKOUT_COPY.priceChangedNote,
} as const;

/** "12 monthly payments of €45.00" / "1 payment of €528.00, today". */
export function paymentsSentence(review: CommitmentReview): string {
  const amount = formatEuros(review.instalmentAmount);
  if (review.paymentMode === "upfront") {
    return `1 payment of ${amount}, today`;
  }
  return `${review.instalmentCount} monthly payments of ${amount}, starting today`;
}

/** How the term ends, in a sentence, or an honest shrug when it is unknown. */
export function endBehaviorSentence(review: CommitmentReview): string {
  if (review.endBehavior === null) return COMMITMENT_CHECKOUT_COPY.endingUnknown;
  if (review.endBehavior === "expire") return COMMITMENT_CHECKOUT_COPY.expires;
  return review.rollMonthlyAmount !== null
    ? COMMITMENT_CHECKOUT_COPY.rollsWithRate(formatEuros(review.rollMonthlyAmount))
    : COMMITMENT_CHECKOUT_COPY.rollsUnknownRate;
}
